import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { History, Search, Filter, Eye, Clock, User, Settings } from 'lucide-react';
import { format } from 'date-fns';

interface AuditLogEntry {
  id: string;
  actor_user_id: string;
  actor_email?: string;
  scope: 'workspace' | 'org';
  scope_id: string;
  scope_name?: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  before: any;
  after: any;
  created_at: string;
}

interface AuditLogViewerProps {
  scope: 'workspace' | 'org';
  scopeId: string;
  logs: AuditLogEntry[];
  isLoading?: boolean;
  onRefresh?: () => void;
}

export function AuditLogViewer({
  scope,
  scopeId,
  logs,
  isLoading = false,
  onRefresh
}: AuditLogViewerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [resourceFilter, setResourceFilter] = useState<string>('all');

  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchQuery || 
      log.actor_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.resource_type.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesAction = actionFilter === 'all' || log.action === actionFilter;
    const matchesResource = resourceFilter === 'all' || log.resource_type === resourceFilter;
    
    return matchesSearch && matchesAction && matchesResource;
  });

  const uniqueActions = Array.from(new Set(logs.map(log => log.action)));
  const uniqueResources = Array.from(new Set(logs.map(log => log.resource_type)));

  const getActionBadgeVariant = (action: string) => {
    switch (action) {
      case 'plan_changed':
        return 'default';
      case 'module_enabled':
      case 'module_disabled':
        return 'secondary';
      case 'bulk_operation':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const formatChangeDetails = (before: any, after: any, action: string) => {
    if (action === 'plan_changed') {
      return `Changed from "${before?.plan_name || 'None'}" to "${after?.plan_name || 'None'}"`;
    }
    
    if (action.includes('module')) {
      const module = after?.module_name || before?.module_name;
      const enabled = after?.enabled ?? before?.enabled;
      return `${module}: ${enabled ? 'Enabled' : 'Disabled'}`;
    }
    
    return 'View details for more information';
  };

  const LogDetailDialog = ({ log }: { log: AuditLogEntry }) => (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Eye className="h-3 w-3" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Audit Log Details</DialogTitle>
          <DialogDescription>
            Complete details for this audit log entry
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2">Basic Information</h4>
              <div className="space-y-2 text-sm">
                <div><strong>Action:</strong> {log.action}</div>
                <div><strong>Resource:</strong> {log.resource_type}</div>
                <div><strong>Timestamp:</strong> {format(new Date(log.created_at), 'PPpp')}</div>
                <div><strong>Actor:</strong> {log.actor_email || log.actor_user_id}</div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Scope</h4>
              <div className="space-y-2 text-sm">
                <div><strong>Type:</strong> {log.scope}</div>
                <div><strong>Name:</strong> {log.scope_name || log.scope_id}</div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2">Before</h4>
              <ScrollArea className="h-32 w-full rounded border p-3">
                <pre className="text-xs">
                  {JSON.stringify(log.before, null, 2)}
                </pre>
              </ScrollArea>
            </div>
            <div>
              <h4 className="font-medium mb-2">After</h4>
              <ScrollArea className="h-32 w-full rounded border p-3">
                <pre className="text-xs">
                  {JSON.stringify(log.after, null, 2)}
                </pre>
              </ScrollArea>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Audit Trail
            </CardTitle>
            <CardDescription>
              Complete history of all entitlement changes for this {scope}
            </CardDescription>
          </div>
          {onRefresh && (
            <Button variant="outline" onClick={onRefresh} disabled={isLoading}>
              Refresh
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by user, action, or resource..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {uniqueActions.map(action => (
                  <SelectItem key={action} value={action}>
                    {action.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={resourceFilter} onValueChange={setResourceFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All resources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All resources</SelectItem>
                {uniqueResources.map(resource => (
                  <SelectItem key={resource} value={resource}>
                    {resource.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Log Entries */}
          <ScrollArea className="h-96">
            <div className="space-y-3">
              {filteredLogs.map((log) => (
                <Card key={log.id} className="hover:bg-muted/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={getActionBadgeVariant(log.action)}>
                            {log.action.replace('_', ' ')}
                          </Badge>
                          <Badge variant="outline">
                            {log.resource_type.replace('_', ' ')}
                          </Badge>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {format(new Date(log.created_at), 'MMM d, HH:mm')}
                          </div>
                        </div>
                        
                        <p className="text-sm">
                          {formatChangeDetails(log.before, log.after, log.action)}
                        </p>
                        
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <User className="h-3 w-3" />
                          {log.actor_email || `User ${log.actor_user_id.slice(0, 8)}...`}
                        </div>
                      </div>
                      
                      <LogDetailDialog log={log} />
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {filteredLogs.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No audit logs found</p>
                  {(searchQuery || actionFilter !== 'all' || resourceFilter !== 'all') && (
                    <p className="text-sm">Try adjusting your filters</p>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
}